import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Pencil } from 'lucide-react';

interface Venue {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  postcode: string;
  country: string;
  commissionPercent: number;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  isActive: boolean;
}

export default function VenueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [venue, setVenue] = useState<Venue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadVenue = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/venues/${id}`);
      if (!res.ok) throw new Error('Failed to load venue');
      const data = await res.json();
      setVenue(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load venue');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadVenue();
  }, [loadVenue]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !venue) {
    return (
      <div className="min-h-screen bg-background">
        <div className="space-y-6">
          <div className="p-4 rounded-md bg-destructive/10 text-destructive">
            {error || 'Venue not found'}
          </div>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/locations/venues')}>
            Back to Venues
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/locations/venues')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold">{venue.name}</h1>
            <Badge variant={venue.isActive ? 'default' : 'secondary'}>
              {venue.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <Button onClick={() => navigate(`/locations/venues/${id}/edit`)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Venue Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Address</p>
              <p className="font-medium">
                {venue.addressLine1}
                {venue.addressLine2 && `, ${venue.addressLine2}`}
              </p>
              <p className="text-muted-foreground">
                {venue.city}, {venue.postcode} {venue.country}
              </p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">Commission</p>
              <p className="font-medium">{venue.commissionPercent}%</p>
            </div>

            {(venue.contactName || venue.contactEmail || venue.contactPhone) && (
              <div>
                <p className="text-sm text-muted-foreground">Contact</p>
                {venue.contactName && <p className="font-medium">{venue.contactName}</p>}
                {venue.contactEmail && (
                  <p className="text-muted-foreground">{venue.contactEmail}</p>
                )}
                {venue.contactPhone && (
                  <p className="text-muted-foreground">{venue.contactPhone}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
